// ✅ File: src/components/admin/escalation/EscalationRules.js
// ✅ Updated: 2025-12-10
// Purpose: Manage Escalation Rules (List + Create/Edit Drawer)
// Compatible with Flask route: /api/escalations/rules
// Includes temporary inline DataTable (no dependency on missing component)

import React, { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import {
  MdAddCircleOutline,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdFilterList,
} from "react-icons/md";
import axiosClient from "../../../api/axiosClient";
import "./escalation.css"; // optional custom styling

// ✅ Temporary DataTable (Bootstrap styled)
// Replace with shared common/DataTable.js once available
const DataTable = ({ columns, data, loading }) => {
  if (loading) return <p className="text-center p-3">Loading...</p>;
  return (
    <div className="table-responsive">
      <table className="table table-hover table-bordered align-middle">
        <thead className="table-primary">
          <tr>
            {columns.map((col) => (
              <th key={col.dataIndex || col.title}>{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.length > 0 ? (
            data.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col.dataIndex || col.title}>
                    {col.render ? col.render(row) : row[col.dataIndex]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center py-3">
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Escalation Rules Main Component
export default function EscalationRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Form state
  const [form, setForm] = useState({
    rule_name: "",
    description: "",
    priority: "Medium",
    escalation_level: 1,
    trigger_condition: "",
    notify_role: "",
    notify_user_ids: "",
    action: "notify",
  });

  // ✅ Fetch Rules
  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/escalations/rules", {
        params: { tenant_id: 1 },
      });
      if (res.data.success) {
        setRules(res.data.data);
      }
    } catch (e) {
      console.error("❌ Failed to fetch rules", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  // ✅ Filter Rules
  const filteredRules = rules.filter((r) =>
    r.rule_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ Table Columns
  const columns = [
    { title: "Rule Name", dataIndex: "rule_name" },
    { title: "Description", dataIndex: "description" },
    { title: "Priority", dataIndex: "priority" },
    { title: "Level", dataIndex: "escalation_level" },
    { title: "Notify Role", dataIndex: "notify_role" },
    { title: "Trigger", dataIndex: "trigger_condition" },
    {
      title: "Action",
      render: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <MdEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
          >
            <MdDelete />
          </Button>
        </div>
      ),
    },
  ];

  // ✅ Handle Add/Edit
  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setForm(rule);
    setShowForm(true);
  };

  const handleAdd = () => {
    setSelectedRule(null);
    setForm({
      rule_name: "",
      description: "",
      priority: "Medium",
      escalation_level: 1,
      trigger_condition: "",
      notify_role: "",
      notify_user_ids: "",
      action: "notify",
    });
    setShowForm(true);
  };

  // ✅ Save Rule
  const handleSave = async () => {
    try {
      const url = selectedRule
        ? `/escalations/rule/${selectedRule.id}`
        : `/escalations/rule`;
      const method = selectedRule ? "put" : "post";

      const res = await axiosClient[method](url, form);
      if (res.data.success) {
        setShowForm(false);
        fetchRules();
      }
    } catch (e) {
      console.error("❌ Save rule failed", e);
    }
  };

  // ✅ Delete Rule
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this rule?")) return;
    try {
      const res = await axiosClient.delete(`/escalations/rule/${id}`);
      if (res.data.success) fetchRules();
    } catch (e) {
      console.error("❌ Delete failed", e);
    }
  };

  // ✅ Pagination Logic
  const paginatedData = filteredRules.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // ✅ Render UI
  return (
    <div className="container-fluid py-3 escalation-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0">⚙️ Escalation Rules</h4>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={fetchRules}>
            <MdRefresh /> Refresh
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <MdAddCircleOutline /> Add Rule
          </Button>
        </div>
      </div>

      {/* ✅ Filter Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <MdFilterList size={20} />
          <input
            type="text"
            placeholder="Search rule..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: "240px" }}
          />
        </div>
        <div className="text-muted small">
          Showing {paginatedData.length} of {filteredRules.length}
        </div>
      </div>

      {/* ✅ Table */}
      <DataTable columns={columns} data={paginatedData} loading={loading} />

      {/* ✅ Pagination */}
      {filteredRules.length > pageSize && (
        <div className="d-flex justify-content-end mt-3">
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  Previous
                </button>
              </li>
              {Array.from({
                length: Math.ceil(filteredRules.length / pageSize),
              }).map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${page === i + 1 ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  page === Math.ceil(filteredRules.length / pageSize)
                    ? "disabled"
                    : ""
                }`}
              >
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* ✅ Modal Form */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRule ? "Edit Escalation Rule" : "Add New Rule"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Rule Name</Form.Label>
                <Form.Control
                  type="text"
                  value={form.rule_name}
                  onChange={(e) =>
                    setForm({ ...form, rule_name: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value })
                  }
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </Form.Select>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Escalation Level</Form.Label>
                <Form.Control
                  type="number"
                  value={form.escalation_level}
                  onChange={(e) =>
                    setForm({ ...form, escalation_level: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Notify Role</Form.Label>
                <Form.Control
                  type="text"
                  value={form.notify_role}
                  onChange={(e) =>
                    setForm({ ...form, notify_role: e.target.value })
                  }
                />
              </div>
              <div className="col-md-12 mb-3">
                <Form.Label>Trigger Condition</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.trigger_condition}
                  onChange={(e) =>
                    setForm({ ...form, trigger_condition: e.target.value })
                  }
                />
              </div>
              <div className="col-md-12 mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
